import Slugger from "github-slugger";
import deburr from "lodash/deburr";
import { getMarkdownFile } from "./MarkdownService";
import enTranslations from "../localizations/en.json";
import svTranslations from "../localizations/sv.json";

const translations = { en: enTranslations, sv: svTranslations };

// Markdown files to index (headings become individual search entries)
const SEARCHABLE_MARKDOWN = [
    // Manual chapters
    { name: "manual_prerequisites", path: "/manual/prerequisites", label: "Manual" },
    { name: "manual_basics", path: "/manual/basics", label: "Manual" },
    { name: "manual_forest_management_planning", path: "/manual/forest-management-planning", label: "Manual" },
    { name: "manual_action_planning", path: "/manual/action-planning", label: "Manual" },
    { name: "manual_drone_operations", path: "/manual/drone-operations", label: "Manual" },
    { name: "manual_advanced", path: "/manual/advanced", label: "Manual" },
    // Resource pages
    { name: "guides", path: "/guides", label: "Guides" },
    { name: "faq", path: "/faq", label: "FAQ" },
    { name: "api", path: "/api", label: "API" },
    // Landing pages
    { name: "bark_beetle", path: "/bark-beetle", label: "Bark Beetle" },
    { name: "final_felling_drone_praxis", path: "/final-felling-drone-praxis", label: "Final Felling Drone Praxis" },
    { name: "kopparfors_windfall", path: "/kopparfors-windfall", label: "Kopparfors Windfall" },
    { name: "pilot", path: "/pilot", label: "Pilot" },
    { name: "planning_drone_praxis", path: "/planning-drone-praxis", label: "Planning Drone Praxis" },
    { name: "retailers", path: "/retailers", label: "Retailers" },
    { name: "sfab_forest_management_planning", path: "/sfab-forest-management-planning", label: "Forest Management Planning (SFAB)" },
    { name: "stora_enso_bark_beetle", path: "/stora-enso-bark-beetle", label: "Stora Enso Bark Beetle" },
    { name: "wildfires_and_prescribed_fire", path: "/fire", label: "Wildfires & Prescribed Fire" },
    { name: "wildlife", path: "/wildlife", label: "Wildlife" },
    { name: "windthrow", path: "/windthrow", label: "Windthrow" },
];

// Home page i18n sections to index.
// Each entry maps a title key and description key to a path anchor.
// Sub-entries list subsection keys that become their own search entries.
const HOME_PAGE_SECTIONS = [
    {
        titleKey: "home-timber-purchases-card-title",
        descKey: "home-timber-purchases-card-text",
        path: "/#timber-purchases",
        subs: [
            { titleKey: "home-timber-purchases-card-section-1-title", descKey: "home-timber-purchases-card-section-1-text" },
            { titleKey: "home-timber-purchases-card-section-2-title", descKey: "home-timber-purchases-card-section-2-text" },
            { titleKey: "home-timber-purchases-card-section-3-title", descKey: "home-timber-purchases-card-section-3-text" },
            { titleKey: "home-timber-purchases-card-section-4-title", descKey: "home-timber-purchases-card-section-4-text" },
        ],
    },
    {
        titleKey: "home-forest-management-card-title",
        descKey: "home-forest-management-card-text",
        path: "/#forest-management",
        subs: [
            { titleKey: "home-forest-management-card-section-1-title", descKey: "home-forest-management-card-section-1-text" },
            { titleKey: "home-forest-management-card-section-2-title", descKey: "home-forest-management-card-section-2-text" },
            { titleKey: "home-forest-management-card-section-3-title", descKey: "home-forest-management-card-section-3-text" },
            { titleKey: "home-forest-management-card-section-4-title", descKey: "home-forest-management-card-section-4-text" },
        ],
        // Customer names are duplicated from the rendering components (ForestManagementSection.js).
        // Keep in sync if a quote's attribution changes.
        testimonials: [
            { key: "home-forest-management-customer-1", customer: "Mats Warensjö, Skogssällskapet" },
            { key: "home-forest-management-customer-2", customer: "Elin Andersson, JGA Skog" },
        ],
    },
    {
        titleKey: "home-damage-inventory-card-title",
        descKey: "home-damage-inventory-card-text",
        path: "/#damage-inventory",
        subs: [
            { titleKey: "home-damage-inventory-card-section-1-title", descKey: "home-damage-inventory-card-section-1-text" },
            { titleKey: "home-damage-inventory-card-section-2-title", descKey: "home-damage-inventory-card-section-2-text" },
            { titleKey: "home-damage-inventory-card-section-3-title", descKey: "home-damage-inventory-card-section-3-text" },
            { titleKey: "home-damage-inventory-card-section-4-title", descKey: "home-damage-inventory-card-section-4-text" },
            { titleKey: "home-damage-inventory-card-section-5-title", descKey: "home-damage-inventory-card-section-5-text" },
        ],
        // Customer names are duplicated from the rendering components. Keep in
        // sync if a quote's attribution changes. Note the two quotes live in
        // different files: customer-1 in Home/DamageInventorySection.js,
        // customer-2 in Home.js.
        testimonials: [
            { key: "home-damage-inventory-customer-1", customer: "Sara Sundin, Stora Enso Sverige" },
            { key: "home-damage-inventory-customer-2", customer: "Peter Semb, Norra Skog" },
        ],
    },
    {
        titleKey: "home-forest-owner-relations-card-title",
        descKey: "home-forest-owner-relations-card-text",
        path: "/#forest-owner-relations",
        subs: [
            { titleKey: "home-forest-owner-relations-card-section-1-title", descKey: "home-forest-owner-relations-card-section-1-text" },
            { titleKey: "home-forest-owner-relations-card-section-2-title", descKey: "home-forest-owner-relations-card-section-2-text" },
            { titleKey: "home-forest-owner-relations-card-section-3-title", descKey: "home-forest-owner-relations-card-section-3-text" },
            { titleKey: "home-forest-owner-relations-card-section-4-title", descKey: "home-forest-owner-relations-card-section-4-text" },
        ],
    },
    {
        titleKey: "home-wildfires-and-prescribed-burning-card-title",
        descKey: "home-wildfires-and-prescribed-burning-card-text",
        path: "/#fire",
    },
    {
        titleKey: "home-bark-beetle-card-title",
        descKey: "home-bark-beetle-card-text",
        path: "/#bark-beetle",
    },
    {
        titleKey: "home-windthrow-card-title",
        descKey: "home-windthrow-card-text",
        path: "/#windthrow",
    },
    {
        titleKey: "home-wildlife-card-title",
        descKey: "home-wildlife-card-text",
        path: "/#wildlife",
    },
    {
        titleKey: "home-final-felling-drone-praxis-card-title",
        descKey: "home-final-felling-drone-praxis-card-text",
        path: "/#final-felling-drone-praxis",
    },
    {
        titleKey: "home-planning-drone-praxis-card-title",
        descKey: "home-planning-drone-praxis-card-text",
        path: "/#planning-drone-praxis",
    },
];

// Standalone i18n content entries (articles, hero, etc.)
const STANDALONE_I18N_ENTRIES = [
    { titleKey: "gf-hero-title-1", descKey: "gf-hero-subtitle", path: "/", label: "Global Forester" },
    { titleKey: "sfab-forest-management-planning-title", descKey: "sfab-forest-management-planning-text", path: "/sfab-forest-management-planning", label: "SFAB" },
    { titleKey: "stora-enso-bark-beetle-title", descKey: "stora-enso-bark-beetle-text", path: "/stora-enso-bark-beetle", label: "Stora Enso" },
    { titleKey: "kopparfors-windfall-title", descKey: "kopparfors-windfall-text", path: "/kopparfors-windfall", label: "Kopparfors" },
    { titleKey: "pilot-title", descKey: "pilot-text", path: "/pilot", label: "Pilot" },
    { titleKey: "manual-introduction-title", descKey: "manual-introduction-text", path: "/manual/introduction", label: "Manual" },
];

// Heading levels that become search entries of their own. The renderer gives
// every heading an anchor; h6 is used twice in the whole site and reads better
// as part of its parent section.
const MAX_INDEXED_HEADING_LEVEL = 5;

// Raw <h1>-<h6> written as HTML, which the Forestand chapters use inside
// <summary> elements. rehype-raw turns them into real headings, so they get
// anchors — and duplicate counters — like any other heading.
const RAW_HEADING = /<h([1-6])[^>]*>([\s\S]*?)<\/h\1>/gi;

// The renderer slugs a heading's text content, so inline markup is already
// gone by the time github-slugger sees it. Reduce the raw line the same way,
// or the anchor drifts from the page again: a heading with a link would slug
// its URL. This is also what ends up as the result title.
//
// Underscores are only dropped where they form emphasis, never inside a word —
// the Forestand chapters are full of Place_Compartment and the renderer keeps
// those too, since CommonMark has no intraword emphasis for "_".
function headingText(raw) {
    return raw
        .replace(/<[^>]+>/g, "") // <key> tags and other inline HTML
        .replace(/!\[[^\]]*\]\([^)]*\)/g, "") // images render with no text of their own
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links keep their label, not the URL
        .replace(/(^|[^\w])_([^_]+)_(?=[^\w]|$)/g, "$1$2") // _emphasis_
        .replace(/\*{1,2}([^*]+)\*{1,2}/g, "$1") // *emphasis* and **strong**
        .replace(/`([^`]*)`/g, "$1") // inline code
        .trim();
}

// Mirrors the anchors rehype-slug-custom-id generates in MarkdownWithTOC:
// github-slugger for the slug, lodash deburr for `removeAccents: true`. A
// fresh Slugger per file keeps the -1, -2 … suffixes on repeated headings in
// sync with the rendered page. Rolling our own here is what made search link
// to anchors that did not exist.
function createSlugger() {
    const slugger = new Slugger();
    return (heading) => deburr(slugger.slug(heading));
}

// Result rows have room for one short line of context. Two segments is the
// most that fits: the chapter a section belongs to, plus its immediate parent
// when that adds something.
function breadcrumb(ancestors, chapterLabel) {
    if (ancestors.length === 0) return chapterLabel;
    const root = ancestors[0].title;
    const parent = ancestors[ancestors.length - 1].title;
    return parent === root ? root : `${root} › ${parent}`;
}

function stripMarkdown(text) {
    return text
        .replace(/<[^>]+>/g, "")
        .replace(/!\[.*?\]\(.*?\)/g, "")
        .replace(/\[([^\]]+)\]\(.*?\)/g, "$1")
        .replace(/[`*_~>#|]/g, "")
        .trim();
}

function parseMarkdownSections(text, basePath, chapterLabel) {
    const lines = text.split("\n");
    const sections = [];
    const slugFor = createSlugger();
    const ancestors = []; // headings still open above the current one
    let currentHeading = null;
    let contentLines = [];
    let preHeadingLines = []; // content before the first heading (or whole file if no headings)

    const flush = () => {
        if (!currentHeading) return;
        // Index full content — display is truncated by line-clamp in the UI.
        const description = contentLines
            .map(stripMarkdown)
            .filter(Boolean)
            .join(" ");
        sections.push({
            title: currentHeading.title,
            description,
            chapter: currentHeading.chapter,
            path: `${basePath}#${currentHeading.id}`,
        });
        contentLines = [];
    };

    for (const line of lines) {
        const hMatch = line.match(/^(#{1,6}) (.+)/);
        if (hMatch) {
            const level = hMatch[1].length;
            const title = headingText(hMatch[2]);
            const id = slugFor(title);

            if (level <= MAX_INDEXED_HEADING_LEVEL) {
                flush();
                while (ancestors.length > 0 && ancestors[ancestors.length - 1].level >= level) {
                    ancestors.pop();
                }
                currentHeading = { title, id, chapter: breadcrumb(ancestors, chapterLabel) };
                ancestors.push({ level, title });
            } else if (currentHeading) {
                // Deeper headings stay searchable as part of their parent section.
                contentLines.push(title);
            }
            continue;
        }

        // Advance the slugger past raw HTML headings too, in document order, so
        // its duplicate counter matches the rendered page. They are not entries
        // of their own — their text is picked up as content below.
        RAW_HEADING.lastIndex = 0;
        let rawMatch;
        while ((rawMatch = RAW_HEADING.exec(line)) !== null) {
            slugFor(headingText(rawMatch[2]));
        }

        if (line.trim() && !line.startsWith("|") && !line.startsWith("!")) {
            if (currentHeading) {
                contentLines.push(line.trim());
            } else {
                // Capture content that appears before any heading.
                preHeadingLines.push(line.trim());
            }
        }
    }
    flush();

    // Fallback: if the file has no headings, or has content before the first
    // heading (intro paragraphs), index that content as a single entry using
    // the chapter label as title. Without this, headerless landing pages
    // (e.g. press-release-style articles) are silently dropped.
    if (preHeadingLines.length > 0) {
        const description = preHeadingLines
            .map(stripMarkdown)
            .filter(Boolean)
            .join(" ");
        if (description) {
            sections.push({
                title: chapterLabel,
                description,
                chapter: chapterLabel,
                path: basePath,
            });
        }
    }

    return sections;
}

/**
 * Build search entries from i18n home page sections.
 */
function buildHomePageEntries(language) {
    const t = translations[language] || translations["en"];
    const label = language === "sv" ? "Startsidan" : "Home";
    const entries = [];

    for (const section of HOME_PAGE_SECTIONS) {
        const title = t[section.titleKey];
        const desc = t[section.descKey] || "";
        if (!title) continue;

        entries.push({ title, description: desc, chapter: label, path: section.path });

        if (section.subs) {
            for (const sub of section.subs) {
                const subTitle = t[sub.titleKey];
                const subDesc = t[sub.descKey] || "";
                if (!subTitle) continue;
                entries.push({ title: subTitle, description: subDesc, chapter: title, path: section.path });
            }
        }

        if (section.testimonials) {
            for (const { key, customer } of section.testimonials) {
                const text = t[key];
                if (!text) continue;
                // Prepend customer name so it's searchable (names are hardcoded
                // in the rendering components, not in i18n files).
                const description = customer ? `${customer}: ${text}` : text;
                entries.push({ title, description, chapter: label, path: section.path });
            }
        }
    }

    for (const entry of STANDALONE_I18N_ENTRIES) {
        const title = t[entry.titleKey];
        const desc = t[entry.descKey] || "";
        if (!title) continue;
        entries.push({ title, description: desc, chapter: entry.label, path: entry.path });
    }

    return entries;
}

// The languages the site search covers. Results are gathered from all of them
// so a term only used in one language still finds its page.
export const SEARCH_LANGUAGES = ["sv", "en"];

// One cache entry per language. A single slot used to be thrown away on every
// other lookup now that both languages are indexed side by side.
const sectionCache = new Map();

export async function fetchAllSections(language) {
    if (sectionCache.has(language)) {
        return sectionCache.get(language);
    }

    const markdownResults = await Promise.all(
        SEARCHABLE_MARKDOWN.map(async ({ name, path, label }) => {
            const url = getMarkdownFile(`${name}_${language}`);
            if (!url) return [];
            const res = await fetch(url);
            const text = await res.text();
            return parseMarkdownSections(text, path, label);
        })
    );

    const homeEntries = buildHomePageEntries(language);

    // Deduplicate based on title + path + description. Multiple sources
    // (markdown headings, i18n home page entries) can produce overlapping
    // entries. Description is part of the key so testimonials — which share
    // their parent section's title and path but carry unique text — survive.
    const seen = new Set();
    const allEntries = [...markdownResults.flat(), ...homeEntries];
    const sections = allEntries
        .filter((entry) => {
            const key = `${entry.title}|${entry.path}|${entry.description}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        // Carried into the result rows: an entry from a language other than the
        // one being viewed is labelled, and opening it switches language first,
        // since its anchor only exists in that rendering.
        .map((entry) => ({ ...entry, language }));

    sectionCache.set(language, sections);
    return sections;
}
